import { NO_MODERATION_END } from "odyssey-shared";
import { say } from "../../words.ts";
import type { CloseRow } from "../disconnect.ts";
import { banSentence, whenWords } from "./words.ts";

/**
 * What a player a GM acted on is shown (ADR-056's amendment of 2026-10-04, item 9), owned by the page rather than
 * by React, on the HUD store's pattern: its snapshot is replaced on each change and kept otherwise. A warning is a
 * notice the player must dismiss, the older first when two arrive. A freeze stands until its end or the gateway's
 * release: while it stands the page sends no movement, since the gateway drops it, and its notice shows whenever no
 * warning does. A kick's or a ban's reason arrives just before the close (4008) and is kept for the notice that
 * close shows. The connection's end forgets all of it; a freeze that still stands is told again at the next join.
 * While the page's own character reads held, the freeze's notice and the release's line say that nothing can harm it
 * (`setHeld`).
 */

/**
 * A notice shown over the game: a warning with its button, or a freeze with none.
 */
export interface ShownNotice {
  readonly kind: "warn" | "freeze";
  readonly title: string;
  readonly sentence: string;
  readonly action: string | null;
}

/**
 * What the notice layer shows.
 */
export interface NoticesSnapshot {
  readonly notice: ShownNotice | null;
}

/**
 * Timers the notices schedule a freeze's end on, injected so tests fire them.
 */
export interface NoticeTimers {
  readonly set: (run: () => void, delayMs: number) => unknown;
  readonly clear: (handle: unknown) => void;
}

/**
 * What the notices run with: the page's clock; how a moment is written (`whenWords` unless given); where a line
 * of plain information goes, the chat log; and the timers.
 */
export interface ModerationNoticesOptions {
  readonly now?: () => number;
  readonly when?: (at: number) => string;
  readonly say?: (text: string) => void;
  readonly timers?: NoticeTimers;
}

const PAGE_TIMERS: NoticeTimers = {
  set: (run, delayMs) => setTimeout(run, delayMs),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

const NOTHING: NoticesSnapshot = Object.freeze({ notice: null });

/**
 * The notices.
 */
export class ModerationNotices {
  private readonly listeners = new Set<() => void>();
  private readonly freezeListeners = new Set<() => void>();
  private readonly now: () => number;
  private readonly when: (at: number) => string;
  private readonly timers: NoticeTimers;
  private snapshot: NoticesSnapshot = NOTHING;
  private warnings: string[] = [];
  private freeze: { readonly until: number; readonly reason: string; readonly timer: unknown } | null = null;
  private kept: CloseRow | null = null;
  private held = false;

  /**
   * @param options The clock, the writer of moments, the chat log's line and the timers.
   */
  public constructor(private readonly options: ModerationNoticesOptions = {}) {
    this.now = options.now ?? Date.now;
    this.when = options.when ?? whenWords;
    this.timers = options.timers ?? PAGE_TIMERS;
  }

  /**
   * Hears that the snapshot changed. Works unbound, as React calls it.
   *
   * @param listener Called on each change.
   * @returns Stops hearing.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Hears a freeze begin or end, whatever notice is showing: a freeze that arrives behind a warning changes nothing
   * shown, so `subscribe` does not tell of it. For a surface that must close when its player is frozen. Works
   * unbound.
   *
   * @param listener Called after a freeze begins, is replaced, is released or is forgotten with the connection.
   * @returns Stops hearing.
   */
  public readonly watchFreeze = (listener: () => void): (() => void) => {
    this.freezeListeners.add(listener);
    return () => {
      this.freezeListeners.delete(listener);
    };
  };

  /**
   * What the notice layer shows now. Works unbound, as React calls it.
   *
   * @returns The snapshot, the same object until something changes.
   */
  public readonly getSnapshot = (): NoticesSnapshot => this.snapshot;

  /**
   * Takes a `moderation` message from the gateway.
   *
   * @param kind What happened: `warn`, `freeze`, `release`, `kick` or `ban`; any other word is ignored.
   * @param reason The reason the GM gave, empty for a release.
   * @param endsInMs The time left, 0 for none, `NO_MODERATION_END` for a permanent ban.
   */
  public receive(kind: string, reason: string, endsInMs: number): void {
    switch (kind) {
      case "warn":
        this.warnings = [...this.warnings, reason];
        break;
      case "freeze": {
        this.thaw();
        const timer = this.timers.set(() => this.release(), Math.max(0, endsInMs));
        this.freeze = { until: this.now() + Math.max(0, endsInMs), reason, timer };
        this.tellFreeze();
        break;
      }
      case "release":
        this.release();
        return;
      case "kick":
        this.kept = { title: say("moderation.notice.kickTitle"), sentence: say("moderation.notice.kickSentence", { reason }), action: "reconnect" };
        return;
      case "ban":
        this.kept = { title: say("moderation.notice.banTitle"), sentence: banSentence(reason, endsInMs === NO_MODERATION_END ? null : this.now() + endsInMs, this.when), action: "log-in" };
        return;
      default:
        return;
    }
    this.show();
  }

  /**
   * Tells the notices whether the page's own character reads held, as the snapshot's away state says (`Away.Held`):
   * the map server holds it safe. Only then does the freeze's notice promise that nothing can harm the player, and
   * the release's line its few seconds, so a page that meets a server which does not hold a frozen character never
   * promises a safety that server does not give (ADR-056's amendment "a frozen character is safe", decision 45).
   *
   * @param held Whether the own character reads held now.
   */
  public setHeld(held: boolean): void {
    if (this.held !== held) {
      this.held = held;
      this.show();
    }
  }

  /**
   * The player pressed the warning's button: the warning leaves, and the next one, or a standing freeze's notice,
   * shows. A freeze is not dismissed.
   */
  public readonly dismiss = (): void => {
    if (this.warnings.length > 0) {
      this.warnings = this.warnings.slice(1);
      this.show();
    }
  };

  /**
   * Whether a freeze stands now, which the scene reads each frame to hold the character still.
   *
   * @returns True until the freeze's end or its release.
   */
  public readonly frozen = (): boolean => this.freeze !== null && this.now() < this.freeze.until;

  /**
   * The notice for a close with 4008: the kick's or the ban's, as the gateway told it just before closing.
   *
   * @returns The notice's row, or null when nothing was told.
   */
  public closed(): CloseRow | null {
    return this.kept;
  }

  /**
   * The connection ended: the freeze, the warnings and the kept reason are forgotten.
   */
  public ended(): void {
    if (this.freeze !== null) {
      this.thaw();
      this.tellFreeze();
    }
    this.warnings = [];
    this.kept = null;
    this.held = false;
    this.show();
  }

  /**
   * Ends a standing freeze and says so in the chat log; with none standing, nothing.
   */
  private release(): void {
    if (this.freeze === null) {
      return;
    }
    this.thaw();
    this.tellFreeze();
    this.options.say?.(say(this.held ? "moderation.notice.releasedSafe" : "moderation.notice.released"));
    this.show();
  }

  /**
   * Tells those who watch the freeze that it began or ended.
   */
  private tellFreeze(): void {
    for (const listener of [...this.freezeListeners]) {
      listener();
    }
  }

  /**
   * Forgets the freeze and its timer.
   */
  private thaw(): void {
    if (this.freeze !== null) {
      this.timers.clear(this.freeze.timer);
      this.freeze = null;
    }
  }

  /**
   * Replaces the snapshot when what shows has changed, and tells the listeners.
   */
  private show(): void {
    const warning = this.warnings[0];
    const next: ShownNotice | null =
      warning !== undefined
        ? { kind: "warn", title: say("moderation.notice.warnTitle"), sentence: say("moderation.notice.warnSentence", { reason: warning }), action: say("moderation.notice.warnAction") }
        : this.freeze !== null
          ? { kind: "freeze", title: say("moderation.notice.freezeTitle"), sentence: say(this.held ? "moderation.notice.freezeSentenceSafe" : "moderation.notice.freezeSentence", { reason: this.freeze.reason }), action: null }
          : null;
    const shown = this.snapshot.notice;
    if (next === null ? shown === null : shown !== null && next.kind === shown.kind && next.sentence === shown.sentence) {
      return;
    }
    this.snapshot = next === null ? NOTHING : { notice: next };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
